import { useCallback, useState, useEffect, useRef } from 'react';
import { MapLocationData, DirectionConfig } from '@hero/web-components';
import type { MapContextValue } from '@hero/web-context';

export interface UseMapDirectionsOptions {
  /** Directions service instance */
  directionsService?: google.maps.DirectionsService;
  /** Travel mode (default: DRIVING) */
  travelMode?: google.maps.TravelMode;
  /** Polyline color */
  strokeColor?: string;
  /** Polyline weight */
  strokeWeight?: number;
  /** Polyline opacity */
  strokeOpacity?: number;
  /** Arrow icon spacing in pixels */
  arrowSpacing?: number;
  /** Arrow icon scale */
  arrowScale?: number;
  /** Arrow fill color */
  arrowFillColor?: string;
  /** Arrow stroke color */
  arrowStrokeColor?: string;
  /** Arrow stroke weight */
  arrowStrokeWeight?: number;
  /** Route line type: 'straight' or 'actual_route' */
  routeLineType?: MapContextValue['routeLineType'];
  /** Current editing index (null when not editing) */
  editingIndex?: number | null;
  /** Current locations array */
  locations?: MapLocationData[];
  /** Map instance */
  map?: google.maps.Map | null;
  /** Whether to auto-recalculate when locations change */
  autoRecalculate?: boolean;
}

/**
 * Hook for calculating and rendering route directions with arrow polylines
 * Handles route calculations between origin and destinations
 */
export function useMapDirections(options: UseMapDirectionsOptions = {}) {
  // Safely get default travel mode - use string literal to avoid accessing google before it's ready
  const getDefaultTravelMode = (): google.maps.TravelMode | undefined => {
    if (typeof google !== 'undefined' && google.maps && google.maps.TravelMode) {
      return google.maps.TravelMode.DRIVING;
    }
    return undefined;
  };

  const {
    directionsService,
    travelMode = getDefaultTravelMode(),
    strokeColor = '#4F46E5',
    strokeWeight = 4,
    strokeOpacity = 0.8,
    arrowSpacing = 1500, // Much larger spacing for minimal arrows
    arrowScale = 3.5, // Smaller scale
    arrowFillColor = strokeColor,
    arrowStrokeColor = '#FFFFFF',
    arrowStrokeWeight = 1.5,
    routeLineType = 'actual_route',
    editingIndex = null,
    locations = [],
    map = null,
    autoRecalculate = true,
  } = options;

  const [directions, setDirections] = useState<google.maps.DirectionsResult[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [directionsConfigs, setDirectionsConfigs] = useState<DirectionConfig[]>([]);
  const prevEditingIndexRef = useRef<number | null>(null);
  const directionsKeyRef = useRef<number>(0); // Force remount of DirectionsRenderer when recalculating

  /**
   * Calculate directions from origin to each destination
   */
  const calculateDirections = useCallback(
    async (
      origin: MapLocationData,
      destinations: MapLocationData[],
      service?: google.maps.DirectionsService,
    ) => {
      if (destinations.length === 0) {
        setDirections([]);
        setDirectionsConfigs([]);
        return;
      }

      if (typeof google === 'undefined' || !google.maps || !google.maps.DirectionsService) {
        console.warn('Google Maps API not loaded yet');
        setIsCalculating(false);
        return;
      }

      if (!travelMode) {
        console.warn('Travel mode not available');
        setIsCalculating(false);
        return;
      }

      setIsCalculating(true);

      const directionsServiceInstance =
        service || directionsService || new google.maps.DirectionsService();
      const results: google.maps.DirectionsResult[] = [];
      const configs: DirectionConfig[] = [];

      try {
        // Calculate route from origin to each destination
        const promises = destinations.map(destination => {
          return new Promise<{
            result: google.maps.DirectionsResult | null;
            destination: MapLocationData;
          }>(resolve => {
            directionsServiceInstance.route(
              {
                origin: origin.cords,
                destination: destination.cords,
                travelMode,
              },
              (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result) {
                  resolve({ result, destination });
                } else {
                  console.warn(`Directions request failed: ${status}`);
                  resolve({ result: null, destination });
                }
              },
            );
          });
        });

        const routeResults = await Promise.all(promises);

        // Process results
        routeResults.forEach(({ result, destination }) => {
          if (result) {
            results.push(result);

            // Extract polyline path from route
            const route = result.routes[0];
            if (route) {
              const path: google.maps.LatLngLiteral[] = [];
              route.legs.forEach(leg => {
                leg.steps.forEach(step => {
                  const stepPath = step.path;
                  stepPath.forEach(point => {
                    path.push({ lat: point.lat(), lng: point.lng() });
                  });
                });
              });

              // No arrows - just simple polyline like old version
              configs.push({
                path,
                options: {
                  strokeColor,
                  strokeWeight,
                  strokeOpacity,
                  // No icons/arrows
                },
                arrowConfig: {
                  spacing: arrowSpacing,
                  scale: arrowScale,
                  fillColor: arrowFillColor,
                  strokeColor: arrowStrokeColor,
                  strokeWeight: arrowStrokeWeight,
                },
              });
            }
          }
        });

        setDirections(results);
        setDirectionsConfigs(configs);
        directionsKeyRef.current += 1; // Increment key when new directions are calculated
      } catch (error) {
        console.error('Error calculating directions:', error);
        setDirections([]);
        setDirectionsConfigs([]);
      } finally {
        setIsCalculating(false);
      }
    },
    [
      directionsService,
      travelMode,
      strokeColor,
      strokeWeight,
      strokeOpacity,
      arrowSpacing,
      arrowScale,
      arrowFillColor,
      arrowStrokeColor,
      arrowStrokeWeight,
    ],
  );

  /**
   * Clear all directions
   */
  const clearDirections = useCallback(() => {
    setDirections([]);
    setDirectionsConfigs([]);
    directionsKeyRef.current += 1; // Increment key to force remount
  }, [directions.length]);

  /**
   * Recalculate directions based on current locations
   */
  const recalculateDirections = useCallback(() => {
    if (routeLineType !== 'actual_route' || locations.length < 2 || !map) {
      return;
    }
    const origin = locations[0];
    const destinations = locations.slice(1);
    calculateDirections(origin, destinations);
  }, [routeLineType, locations, map, calculateDirections]);

  // Centralized route line management logic
  useEffect(() => {
    // ALWAYS clear directions when editing (editingIndex !== null)
    // This ensures lines disappear immediately when entering edit mode
    if (editingIndex !== null) {
      clearDirections();
      prevEditingIndexRef.current = editingIndex;
      return undefined;
    }

    // Recalculate directions when exiting edit mode (if auto-recalculate is enabled)
    if (autoRecalculate && prevEditingIndexRef.current !== null) {
      // Just exited edit mode - clear FIRST to remove old lines, then recalculate after a delay
      // Use a longer delay to ensure React has time to unmount old DirectionsRenderer components
      clearDirections();

      // Recalculate after a delay to ensure old lines are removed from DOM
      // Increased delay to 400ms to give React more time to unmount old components
      const timeoutId = setTimeout(() => {
        recalculateDirections();
      }, 400);
      prevEditingIndexRef.current = editingIndex;
      return () => clearTimeout(timeoutId);
    }

    // Update ref for next comparison
    prevEditingIndexRef.current = editingIndex;
    return undefined;
  }, [editingIndex, clearDirections, recalculateDirections, autoRecalculate]);

  // Auto-recalculate when locations change (but not when editing)
  useEffect(() => {
    if (!autoRecalculate || editingIndex !== null) {
      return undefined;
    }

    if (routeLineType !== 'actual_route') {
      // Clear directions if not using actual routes
      if (directions.length > 0) {
        clearDirections();
      }
      return undefined;
    }

    if (locations.length >= 2 && map) {
      // Debounce directions calculation to prevent lag during rapid changes
      const timeoutId = setTimeout(() => {
        const origin = locations[0];
        const destinations = locations.slice(1);
        calculateDirections(origin, destinations);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      // Clear directions if there are not enough locations
      clearDirections();
      return undefined;
    }
  }, [
    locations.length,
    map,
    calculateDirections,
    clearDirections,
    routeLineType,
    directions.length,
    editingIndex,
    autoRecalculate,
  ]);

  return {
    directions,
    directionsConfigs,
    isCalculating,
    calculateDirections,
    clearDirections,
    recalculateDirections,
    directionsKey: directionsKeyRef.current, // Expose key for component remounting
  };
}
